// @ts-strict-ignore
import { isSalaryUnit } from '@backend/app-acquisition-logic';
import { Grid } from '@backend/lib-design-system';
import { Features } from '@backend/lib-feature-mgr';
import type { BusinessUnit } from '@backend/lib-l10n';
import type { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';

import { callAcquisitionRestApi } from '~/api-utils/acquisition';
import { findCityPageName } from '~/business/City/findCityPageName';
import { findExternalJobOfferBySlug } from '~/business/JobOffer/findExternalJobOffersBySlug';
import { getSimilarJobOffers, type SimilarJobOffers } from '~/business/JobOffer/getSimilarJobOffers';
import type { SearchJobOfferOutput } from '~/business/JobOffer/searchJobOffers';
import type { JobOffer, JobOfferWithJobPost } from '~/business/JobOffer/types';
import { Breadcrumbs } from '~/components/Breadcrumbs';
import { Column } from '~/components/Column';
import { Container } from '~/components/Container/Container';
import { IframeContainer } from '~/components/Container/iframeContainer';
import { Head } from '~/components/Head';
import { LatestJobOffersCard } from '~/components/LatestJobOffersCard/LatestJobOffersCard';
import { MissionApplyButton } from '~/components/Mission/ApplyButton';
import { MissionDescription } from '~/components/Mission/Description';
import { MissionExpired, type MissionExpiredProps } from '~/components/Mission/Expired';
import { MissionHeader } from '~/components/Mission/Header';
import { MissionInfo } from '~/components/Mission/Info';
import { MissionMap } from '~/components/Mission/Map';
import { getSimilarOffersTitle } from '~/components/Mission/similarOffersTitle';
import { MobileOnly, NotMobile } from '~/components/Styled';
import { businessUnits } from '~/config/businessUnits';
import { ApplyTypes } from '~/contexts/ApplyFormContext/enums';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import { EVENTS, useTrackingContext } from '~/contexts/TrackingContext';
import { businessUnitGuard } from '~/guards/businessUnitGuard';
import { useAsyncEffect, useCheckFeatureEnabled, useTranslation } from '~/hooks';
import { useHref } from '~/hooks/useHref';
import { useMissionUrl } from '~/hooks/useMissionUrl';
import { getAreaCode } from '~/utils/areaCode';
import { formatCityName } from '~/utils/cityName';
import { getMissionHreflangLinks, type HreflangLink } from '~/utils/hreflang';
import { getJobOfferJsonld } from '~/utils/jsonLD';
import { getMissionMetaDescription } from '~/utils/missionMetaDescription';
import { getMissionPageTitle } from '~/utils/missionPageTitle';
import * as missionUrl from '~/utils/missionUrl';
import { navigateOrReload } from '~/utils/navigation';
import { getParamValue } from '~/utils/params';
import { formatSalary } from '~/utils/salary';
import { sanitize } from '~/utils/sanitize';
import { generateSearchUrlFromJobOffer } from '~/utils/search/generateSearchUrlFromJobOffer';
import { buildTranslate } from '~/utils/translations/buildTranslate';
import { getTranslations } from '~/utils/translations/getTranslations';

// the apply form weighs ~160 KB (phone numbers of every country, reCAPTCHA): loaded when the visitor opens it
const ApplyFormModal = dynamic(() => import('~/components/ApplyFormModal').then(module => module.ApplyFormModal));

type PublishedMissionPageProps = JobOfferWithJobPost & {
  /** the indexed city page of the offer's city, linked from its breadcrumb */
  cityPageName: string | null;
  hreflangLinks?: HreflangLink[];
  similarJobOffers: SimilarJobOffers;
};

type MissionPageProps =
  ({ isExpired?: false } & PublishedMissionPageProps) | ({ isExpired: true } & MissionExpiredProps);

const StickyGridCell = styled.div`
  top: 3rem;
  position: sticky;
  padding-left: 3rem;
`;

const ExtraSpace = styled.div`
  height: 3rem;
`;

const FixedButton = styled.div`
  position: fixed;
  z-index: 1;
  bottom: 0;
  width: calc(100% + 1rem);
  left: -1rem;
  span {
    border-radius: 0 !important;
  }
`;

const PublishedMissionPage: FC<PublishedMissionPageProps> = ({
  jobOffer,
  jobPost,
  cityPageName,
  hreflangLinks,
  similarJobOffers,
}) => {
  const t = useTranslation();
  const { asPath, replace } = useRouter();
  const { businessUnit, country, iframe } = useBusinessUnitContext();
  const { isMissionApplyUrl, missionUrlPath } = useMissionUrl(jobPost.slug);
  const applyCvRequiredFeature = useCheckFeatureEnabled(Features.JobBoardApplyCvRequired);
  const { trackApplyButtonClicked, trackPage, trackJobOfferClicked } = useTrackingContext();
  const { getPageHref } = useHref();

  const breadcrumbs = useMemo(
    () => [
      ...(cityPageName ? [{ label: cityPageName, href: getPageHref('cities', cityPageName) }] : []),
      { label: jobOffer.title },
    ],
    [cityPageName, getPageHref, jobOffer.title],
  );

  const [showApplyModal, setShowApplyModal] = useState(isMissionApplyUrl);

  useEffect(() => {
    trackPage(EVENTS.missionPageLoaded, { jobTitle: jobOffer.title, refId: jobPost.refId });
  }, []);

  useEffect(() => {
    window.parent?.postMessage(JSON.stringify({ type: 'onShowApplyModalChanged', showApplyModal }), '*');
  }, [showApplyModal]);

  useAsyncEffect(async () => {
    let newPath: string;
    const path = asPath.split('?')[0];

    if (isMissionApplyUrl) {
      newPath = missionUrl.getMissionUrlApplyPath(businessUnit, jobPost.slug);
    } else {
      newPath = missionUrl.getMissionUrlPath(businessUnit, jobPost.slug);
    }

    if (path !== newPath) {
      await replace(newPath);
    }
  }, [businessUnit, asPath, jobPost.slug, isMissionApplyUrl]);

  const areaCode = getAreaCode(country, jobOffer.location);

  const { title, description, socialTitle, socialDescription } = useMemo(
    () => ({
      description: getMissionMetaDescription(t, jobOffer),
      title: getMissionPageTitle(t, { title: jobOffer.title, city: jobOffer.location.city, areaCode }),
      socialTitle: t('meta_social_description.mission_title', {
        displayName: jobOffer.title,
        city: jobOffer.location.city,
        areaCode,
      }),
      socialDescription: t('meta_social_description.mission_description', {
        displayName: jobOffer.title,
        city: jobOffer.location.city,
      }),
    }),
    [jobOffer, jobPost],
  );

  const missionLocation = useMemo(
    () =>
      Array.isArray(jobOffer.location.coordinates) &&
      jobOffer.location.coordinates.length === 2 && {
        lng: jobOffer.location.coordinates[0],
        lat: jobOffer.location.coordinates[1],
      },
    [jobOffer],
  );

  const onMissionApplyClick = () => {
    setShowApplyModal(true);
    trackApplyButtonClicked({
      refId: jobPost.refId,
      jobTitle: jobOffer.title,
    });
  };

  const onApplyModalClose = useCallback(async () => {
    await navigateOrReload(replace, missionUrlPath);
    setShowApplyModal(false);
  }, [setShowApplyModal, missionUrlPath]);

  const handleSimilarOfferClick = useCallback(
    (similarOffer: SearchJobOfferOutput) => {
      trackJobOfferClicked({
        refId: similarOffer.refId,
        jobTitle: similarOffer.title,
      });
    },
    [trackJobOfferClicked],
  );

  const container = (
    <Column>
      <MissionHeader jobOffer={jobOffer} jobPost={jobPost} />
      <Grid.Wrapper columnGap="">
        <Grid.Cell xs={1} s={2 / 3}>
          <MissionDescription
            missionIntro={jobOffer.introductionHTML || jobOffer.introduction}
            missionDesc={jobOffer.aboutHTML || jobOffer.about}
            workRates={jobOffer.workRatesHTML}
            missionBonuses={jobOffer.bonusesHTML || jobOffer.bonuses}
            missionPrerequisites={jobOffer.prerequisitesHTML || jobOffer.prerequisites}
            missionLegalInfos={jobOffer.legalInfosHTML || jobOffer.legalInfos}
            requiredExperience={jobOffer.requiredExperienceLabel}
            requiredCertificates={jobOffer.requiredCertificatesLabel}
          />
          {missionLocation && !jobOffer.applyLink && (
            <NotMobile>
              <MissionMap jobOffer={jobOffer} />
            </NotMobile>
          )}
          <MobileOnly>
            {iframe && <ExtraSpace></ExtraSpace>}
            <FixedButton>
              <MissionApplyButton onClick={onMissionApplyClick} />
            </FixedButton>
          </MobileOnly>
        </Grid.Cell>
        <Grid.Cell xs={0} s={1 / 3}>
          <StickyGridCell>
            <MissionInfo {...jobOffer} onMissionApplyClick={onMissionApplyClick} />
          </StickyGridCell>
        </Grid.Cell>
      </Grid.Wrapper>
    </Column>
  );

  return (
    <>
      <Head
        title={title}
        description={description}
        socialTitle={jobOffer.title && jobOffer.location.city && areaCode && socialTitle}
        socialDescription={jobOffer.title && jobOffer.location.city && socialDescription}
        hreflangLinks={hreflangLinks}
      />
      {iframe ? (
        <IframeContainer removeTopMargin>{container}</IframeContainer>
      ) : (
        <>
          <Breadcrumbs items={breadcrumbs} />
          <Container>{container}</Container>
        </>
      )}
      <LatestJobOffersCard
        latestJobOffers={similarJobOffers.jobOffers}
        title={getSimilarOffersTitle(t, similarJobOffers, { title: jobOffer.title, city: jobOffer.location.city })}
        onMissionClick={handleSimilarOfferClick}
        hideButton
      />
      {showApplyModal && (
        <ApplyFormModal
          applyType={ApplyTypes.jobOffer}
          jobOffer={jobOffer}
          jobPost={jobPost}
          onClose={onApplyModalClose}
          isIframe={iframe}
          isCvRequired={applyCvRequiredFeature}
        />
      )}
    </>
  );
};

const MissionPage: FC<MissionPageProps> = props => {
  const { iframe } = useBusinessUnitContext();

  if (props.isExpired === true) {
    return iframe ? (
      <IframeContainer>
        <MissionExpired {...props} />
      </IframeContainer>
    ) : (
      <MissionExpired {...props} />
    );
  }
  return <PublishedMissionPage {...props} />;
};

// 410 rather than a redirect: search engines drop a gone offer at once, while a permanent redirect to a search page
// is crawled again and again and reported as a soft 404
const getExpiredMissionProps = async (
  context: GetServerSidePropsContext,
  { businessUnit, jobOffer, refId }: { businessUnit: BusinessUnit; jobOffer: JobOffer; refId: string },
) => {
  const similarJobOffers = await getSimilarJobOffers({
    businessUnit,
    jobTitle: jobOffer.title,
    coordinates: jobOffer.location.coordinates,
    excludeRefId: refId,
  });

  context.res.statusCode = 410;

  return {
    props: {
      isExpired: true,
      title: jobOffer.title,
      city: jobOffer.location.city ?? null,
      searchUrl: generateSearchUrlFromJobOffer(jobOffer, businessUnit, businessUnits[businessUnit].country),
      similarJobOffers,
    } satisfies MissionPageProps,
  };
};

// Errors are not caught: a 5xx tells search engines to come back, where a redirect or a 404 would drop the offer
export const getServerSideProps = businessUnitGuard<MissionPageProps>(async (businessUnit, context) => {
  const { country, currency, language } = businessUnits[businessUnit];
  const slug = getParamValue(context.params.slug);

  const props = await callAcquisitionRestApi({
    method: 'getJobOfferForJobs',
    body: { slug },
    businessUnit,
    idempotent: true,
  });

  if (props) {
    const { jobPost } = props;
    const jobOffer = {
      ...props.jobOffer,
      location: { ...props.jobOffer.location, city: formatCityName(businessUnit, props.jobOffer.location.city) },
    };

    if (!jobPost.published) {
      return getExpiredMissionProps(context, { businessUnit, jobOffer, refId: jobPost.refId });
    }

    // the offer was republished under a new slug: one URL per offer, the one in the sitemap
    if (jobPost.slug !== slug) {
      // resolvedUrl is rebuilt from the rewrite destination, which drops the apply suffix; req.url keeps it
      // (with a `.json` suffix on a client-side navigation)
      const requestPath = (context.req.url ?? '').split('?')[0].replace(/\.json$/, '');
      return {
        redirect: {
          destination: missionUrl.isMissionApplyUrl(businessUnit, requestPath)
            ? missionUrl.getMissionUrlApplyPath(businessUnit, jobPost.slug)
            : missionUrl.getMissionUrlPath(businessUnit, jobPost.slug),
          permanent: true,
        },
      };
    }

    const [similarJobOffers, cityPageName] = await Promise.all([
      getSimilarJobOffers({
        businessUnit,
        jobTitle: jobOffer.title,
        coordinates: jobOffer.location.coordinates,
        excludeRefId: jobPost.refId,
      }),
      findCityPageName(businessUnit, jobOffer.location),
    ]);

    return {
      props: {
        jobOffer: {
          ...jobOffer,
          introductionHTML: sanitize(jobOffer.introductionHTML || jobOffer.introduction || ''),
          aboutHTML: sanitize(jobOffer.aboutHTML || jobOffer.about || ''),
          workRatesHTML: sanitize(jobOffer.workRatesHTML || ''),
          bonusesHTML: sanitize(jobOffer.bonusesHTML || jobOffer.bonuses || ''),
          prerequisitesHTML: sanitize(jobOffer.prerequisitesHTML || jobOffer.prerequisites || ''),
          legalInfosHTML: sanitize(jobOffer.legalInfosHTML || jobOffer.legalInfos || ''),
        },
        jobPost,
        cityPageName,
        hreflangLinks: getMissionHreflangLinks(businessUnit, slug),
        similarJobOffers,
        jsonld: getJobOfferJsonld({ jobOffer, jobPost, businessUnit, country }),
      },
    };
  }

  const translations = getTranslations(businessUnit);
  const t = buildTranslate(translations, businessUnit);

  const externalJobOffer = await findExternalJobOfferBySlug({ slug, businessUnit });

  if (!externalJobOffer) {
    return { notFound: true };
  }

  const unitRate = externalJobOffer.salary?.unitRate;

  const validUnitRate = isSalaryUnit(unitRate) ? unitRate : undefined;

  const salary = {
    minRate: externalJobOffer.salary?.minRate ?? undefined,
    maxRate: externalJobOffer.salary?.maxRate ?? 0,
    currencyCode: currency as string,
    unitRate: validUnitRate,
  };

  const jobOfferData: JobOffer = {
    id: externalJobOffer.id,
    type: externalJobOffer.type,
    typeLabel: t(`form_option_labels.type.${externalJobOffer.type.toLowerCase()}`),
    title: externalJobOffer.title,
    location: { ...externalJobOffer.location, city: formatCityName(businessUnit, externalJobOffer.location.city) },
    requiredExperienceLabel: externalJobOffer.requiredExperience
      ? t(`form_option_labels.type.${externalJobOffer.requiredExperience.toLowerCase()}`)
      : null,
    salary,
    salaryFormattedCurrency: salary.unitRate ? formatSalary({ locale: language, salary, currency }) : null,
    salaryFormattedUnit: salary.unitRate ? t(`salary.gross_per_unit.${salary.unitRate.toLowerCase()}`) : null,
    weeklyHours: externalJobOffer.weeklyHours || null,
    applyLink: externalJobOffer.applyLink || null,
    about: '',
    aboutHTML: externalJobOffer.descriptionHtml,
    introduction: '',
    introductionHTML: '',
    bonuses: '',
    bonusesHTML: '',
    prerequisites: '',
    prerequisitesHTML: '',
    legalInfos: '',
    legalInfosHTML: '',
    workRatesHTML: null,
    requiredCertificates: [],
    requiredCertificatesLabels: [],
    requiredCertificatesLabel: null,
    monthsOfExperience: null,
  };

  const [externalJobPost] = externalJobOffer.jobPosts;

  if (!externalJobPost.publishedAt) {
    return getExpiredMissionProps(context, { businessUnit, jobOffer: jobOfferData, refId: externalJobPost.refId });
  }

  const [similarJobOffers, cityPageName] = await Promise.all([
    getSimilarJobOffers({
      businessUnit,
      jobTitle: jobOfferData.title,
      coordinates: jobOfferData.location.coordinates,
      excludeRefId: externalJobPost.refId,
    }),
    findCityPageName(businessUnit, jobOfferData.location),
  ]);

  const jobPost = {
    slug: externalJobPost.slug,
    published: true,
    refId: externalJobPost.refId,
    publishedAt: externalJobPost.publishedAt,
    discardedAt: null,
  };

  return {
    props: {
      jobOffer: jobOfferData,
      jobPost,
      cityPageName,
      similarJobOffers,
      jsonld: getJobOfferJsonld({ jobOffer: jobOfferData, jobPost, businessUnit, country }),
    },
  };
});

export default MissionPage;
